import {useState} from 'react';
import StepFormTemplate from "../../templates/step-form.template";
import {NumberVerificationTemplate} from '../../templates/number-verification.template';
import {useNavigate} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {checkUserService, createUserService} from '../../../services/new-services/auth-v2.services';
import {updateAuthToken} from '../../../redux/slices/application-process.slice';
import {requestPhoneOTPService, validatePhoneOTPService} from '../../../services/new-services/otp-v2.services';
import {toast} from 'react-toastify';
import {createWebLogRecord} from "../../../services/new-services/analytics.service.js";
import {jwtDecode} from "jwt-decode";
import {
  createFirstTimeApplicationService,
  createUserAddress,
  setUserPersonalInfoService
} from "../../../services/new-services/application-v2.services.js";
import {getPublicIP} from "../../../utils/url.js";
import CredoAppSdkService from "../../../services/credoappsdk.services.js";
import {v4 as uuidv4} from "uuid";
import { useAnalytics } from '../../../hooks/useAnalytics.js';


const NumberVerificationScreen = () => {
  const analytics = useAnalytics();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authToken = useSelector(state => state.applicationProcess.authToken);
  const sessionId = useSelector(state => state.applicationProcess.sessionId);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [isPhoneInUse, setIsPhoneInUse] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resetToken, setResetToken] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [isVerifying, setIsVerifying] = useState(false);
  const addressInfo = useSelector((state) => state.applicationProcess.address);
  const personalInfo = useSelector((state) => state.applicationProcess.personalInformation);
  const generalInfo = useSelector((state) => state.applicationProcess.generalInformation);
  const marketingMetadata = useSelector((state) => state.applicationProcess.marketingMetadata);
  const referralCode = useSelector((state) => state.applicationProcess.referralCode);
  const [applicationId, setApplicationId] = useState(null);
  const [addressCreated, setAddressCreated] = useState(false);
  const [personalInfoCreated, setPersonalInfoCreated] = useState(false);

  const handlePhoneNumberInput = (number) => {
    setPhoneNumber(number);
    setIsPhoneInUse(false);
  };

  async function handleSubmit(userId, currentAuthToken){

    // create personal information
    if (!personalInfoCreated) {
      const personalResponse = await setUserPersonalInfoService(currentAuthToken, personalInfo);
      if (!personalResponse.success) {
        createWebLogRecord(sessionId, null, 
          personalInfo, true, false, 'PERSONAL_INFORMATION_SET_WEB');
        console.log('Error setting personal info');
        return;
      } else {
        createWebLogRecord(sessionId, null, 
          personalInfo, false, true, 'PERSONAL_INFORMATION_SET_WEB');
      }
      setPersonalInfoCreated(true);
    }

    // create address information
    if (!addressCreated) {
      const addressResponse = await createUserAddress(currentAuthToken, addressInfo);
      createWebLogRecord(sessionId, userId, addressInfo, !addressResponse.success, addressResponse.success, 'ADDRESS_CREATED_WEB');
      if (!addressResponse.success) {
        console.log('Error creating address');
        return;
      }
      setAddressCreated(true);
    }

    if (!applicationId) {
      const applicationPayload = {
        occupationId: generalInfo.userOccupation?.id,
        loanUsageId: generalInfo.loanUsage?.id,
        loanReferrerId: referralCode?.toUpperCase(),
        marketingData: marketingMetadata,
        deviceInfo: {
          location: null,
          ip: await getPublicIP(),
          contacts: null,
        },
        foundByAnswer: 'web',
      }
      const applicationResponse = await createFirstTimeApplicationService(currentAuthToken, applicationPayload);
      createWebLogRecord(sessionId, userId, applicationPayload, !applicationResponse.success, applicationResponse.success, 'APPLICATION_CREATED_WEB');
      if (!applicationResponse.success) {
        return;
      }else{
        analytics.sendEvent({
          event: "Contact",
          event_category: "FormSubmission",
        });
        analytics.sendEvent({
            event: "CompleteRegistration",
            event_category: "FormSubmission",
        });
        window.gtag("event", "conversion", {
            send_to: "AW-11111897320/0eNnCKS9vZcYEOixyLIp",
        });
      }
      setApplicationId(applicationResponse.application.id);
    }
  }

  async function handlePhoneVerification(messageType = 'whatsapp') {
    setIsLoading(true);
    let currentAuthToken = authToken;
    if (!currentAuthToken) {
      const response = await checkUserService(phoneNumber);
      if (response.success && response.data.hasAccount) {
        setIsPhoneInUse(true);
        setIsLoading(false);
        return;
      }
      let creationResponse = await createUserService(phoneNumber);
      if (!creationResponse.success) {
        createWebLogRecord(sessionId, null, 
          {phoneNumber: phoneNumber}, true, false, 'USER_CREATED_WEB');
        setIsLoading(false);
        return;
      }
      currentAuthToken = creationResponse.data.accessToken;
      createWebLogRecord(sessionId, null, 
        {phoneNumber: phoneNumber}, false, true, 'USER_CREATED_WEB');
      const userId = jwtDecode(currentAuthToken).id;
      await handleSubmit(userId, currentAuthToken);
      dispatch(updateAuthToken(currentAuthToken));
    }
    let requestResponse = await requestPhoneOTPService(currentAuthToken, phoneNumber, messageType);
    if (!requestResponse.success) {
      setIsLoading(false);
      createWebLogRecord(sessionId, null, 
        {phoneNumber: phoneNumber}, true, false, 'USER_REQUEST_OTP');
      return;
    } else {
      createWebLogRecord(sessionId, null, 
        {phoneNumber: phoneNumber}, false, true, 'USER_REQUEST_OTP');
    }
    setResetToken(requestResponse.data.accessToken);
    setCurrentStep(1);
    setIsLoading(false);
    return true;
  }

  const handleOtpCodeInput = (code) => {
    setOtpCode(code);
    if (code.length === 5 && !isVerifying) { // Prevent multiple calls
      verifyCode(code);
    }
  };

  function getCurrentDescription() {
    if (currentStep === 0) {
      return 'Te enviaremos un código de verificación. Asegúrate de que el número esté correcto';
    } else if (currentStep === 1) {
      const formatted = phoneNumber.replace(
        /(\d{2})(\d{4})(\d{0,4})/,
        (match, p1, p2, p3) => `+52 ${p1} ${p2} ${p3}`.trim()
      );
      return `Ingresa el código enviado al ${formatted}`;
    } else if (currentStep === 2) {
      return 'Número verificado exitosamente';
    }
  }

  const verifyCode = async (code) => {
    if (isVerifying) return; // Prevent duplicate calls
    
    setIsVerifying(true);
    setIsLoading(true);
    
    const response = await validatePhoneOTPService(authToken, code, resetToken);
    
    if (!response.success) {
      toast.error("Error al verificar código", { autoClose: 1500 });
      setOtpCode(''); // Clear the input so user can try again
      setIsLoading(false);
      setIsVerifying(false);
      createWebLogRecord(sessionId, null, 
        {code: code, error: response.error}, true, false, 'PHONE_VERIFIED_WEB');
      return;
    }
    createWebLogRecord(sessionId, null, 
      {code: code}, false, true, 'PHONE_VERIFIED_WEB');
    dispatch(updateAuthToken(response.data.accessToken));
    setIsLoading(false);
    setPhoneVerified(true);
    setCurrentStep(2);
    setIsVerifying(false);
  };

  const handleNext = async () => {
    setIsLoading(true);
    const userId = jwtDecode(authToken).id;
    await handleSubmit(userId, authToken);
    CredoAppSdkService.stopTrackingAndComplete(uuidv4(), applicationId, authToken);
    setIsLoading(false);
    navigate('/app/application/evaluation');
  };

  return (
    <div style={styles.mainContainerStyle}>
      <StepFormTemplate
        title="Verifica tu número de celular"
        currentStep={5}
        totalSteps={5}
        submitEnabled={phoneVerified}
        buttonTitle="Finalizar Solicitud"
        onSubmit={handleNext}
        description={getCurrentDescription()}
        onClose={() => navigate('/')}
      >
        <NumberVerificationTemplate
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          isLoading={isLoading}
          phoneNumber={phoneNumber}
          onPhoneNumberInput={handlePhoneNumberInput}
          otpCode={otpCode}
          onOtpCodeInput={handleOtpCodeInput}
          isPhoneInUse={isPhoneInUse}
          onRequestVerificationCode={() => handlePhoneVerification('whatsapp')}
          onRequestVerificationCodeSMS={() => handlePhoneVerification('sms')}
        />
      </StepFormTemplate>
    </div>
  );
};


const styles = {
  mainContainerStyle: {
      margin: '0 auto',
      padding: window.innerWidth > 700 ? '0 25%' : '0'
  }
}


export default NumberVerificationScreen;